import { IComponentOptions, IOnChangesObjectOf, IQService, IScope } from "angular";
import { GtmhubController } from "@gtmhub/core";
import { getSessionAndOneLevelChildren, IPlanningSession, ISessionsStoreState, PlanningSessionsActions, PlanningSessionStatus } from "@gtmhub/sessions";
import { IGoal } from "@gtmhub/goals/models";
import { INgRedux } from "@gtmhub/state-management";
import { IExtendedRootScopeService } from "@gtmhub/shared/ng-extensions";
import { IUIError, UIErrorHandlingService } from "@gtmhub/error-handling";
import { getCurrentUserId, IUser, UserService } from "@gtmhub/users";
import { byEndDescAndStartAsc } from "../session-tree-selector/utils";
import { allowedActionsSync } from "@gtmhub/permissions/utils";
import { IMetric } from "@gtmhub/okrs/metrics";

const bindStateToCtrl = (state: ISessionsStoreState): ISessionSelectorDropdownRedux => {
  const { items: sessions, map: sessionIdMap, isFetched: sessionsFetched, error: sessionsError } = state.sessions;

  return {
    sessionIdMap: sessionIdMap,
    sessions: sessions,
    reduxItemsFetched: sessionsFetched,
    reduxItemsError: sessionsError,
  };
};

interface IPlanningSessionWithChildren extends IPlanningSession {
  children: IPlanningSessionWithChildren[];
}

interface ISessionSelectorDropdownRedux {
  sessionIdMap: Record<string, IPlanningSession>;
  sessions: IPlanningSession[];
  reduxItemsFetched: boolean;
  reduxItemsError: IUIError;
}

export interface ISessionSelectorDropdownCtrlScope extends IExtendedRootScopeService, IScope, ISessionSelectorDropdownRedux {}

export interface ISessionSelectorDropdownComponentBindings {
  currentSession: IPlanningSession;
  goal: IGoal;
  parentItem: IGoal | IMetric;
  enableItemDisabledState: boolean;
  onSelection(args: { sessionId: string }): void;
}

export interface SessionSelectorDropdownCtrl extends ISessionSelectorDropdownComponentBindings, ISessionSelectorDropdownRedux {
  currentUser: IUser;
  allowDropdownToggle: boolean;
  isSearchInputVisible: boolean;
  sessionSearch: string;
  sessionIdMap: Record<string, IPlanningSession>;
  sessions: IPlanningSession[];
  enableItemDisabledState: boolean;
  filterWithHostId: boolean;
  subTreeBuild: boolean;
  loading: boolean;
}
export class SessionSelectorDropdownCtrl extends GtmhubController {
  public static $inject = ["$q", "$scope", "$ngRedux", "PlanningSessionsActions", "UIErrorHandlingService", "UserService", "GoalService", "MetricService"];

  constructor(
    private $q: IQService,
    private $scope: ISessionSelectorDropdownCtrlScope,
    private $ngRedux: INgRedux,
    private sessionsActions: PlanningSessionsActions,
    private uiErrorHandlingService: UIErrorHandlingService,
    private userService: UserService
  ) {
    super();
    this.onDestroy(this.$ngRedux.connect(bindStateToCtrl)(this));
  }

  $onInit(): void {
    this.$ngRedux.dispatch(this.sessionsActions.getSessionsIfMissing());
    this.loading = true;
    this.allowDropdownToggle = true;
    this.subTreeBuild = false;

    this.$q
      .all({
        _: this.$scope.watchUntilAndRejectOnError(
          () => this.reduxItemsFetched,
          () => this.reduxItemsError
        ),
        currentUser: this.userService.getUser(getCurrentUserId()),
      })
      .then(
        ({ currentUser }) => {
          this.currentUser = currentUser;
          this.loadData();
        },
        (err) => this.uiErrorHandlingService.handleIndicator(err, { indicator: "loading", inline: false })
      );
  }

  $onChanges(onChangesObj: IOnChangesObjectOf<ISessionSelectorDropdownComponentBindings>) {
    const { parentItem } = onChangesObj;
    if (parentItem && !parentItem.isFirstChange()) {
      if (parentItem.previousValue && !parentItem.currentValue) {
        this.sessions = Object.values(this.sessionIdMap);
      } else {
        const parentItemSession = this.sessionIdMap[parentItem.currentValue.sessionId];
        this.sessions = [
          parentItemSession,
          ...getChildren(parentItemSession as IPlanningSessionWithChildren, Object.values(this.sessionIdMap) as IPlanningSessionWithChildren[]),
        ];
      }
      this.loadData();
    }
  }

  private loadData(): void {
    this.filterWithHostId = this.goal?.parentId ? true : false;
    this.sessionSearch = "";
    this.isSearchInputVisible = false;

    // If we are inside edit objective view where the session is LOCKED we want to restrict all operations with the session selector
    const shouldRestrictOperationsWithSessionSelector = this.currentSession && this.currentSession.status !== PlanningSessionStatus.OPEN;
    if (shouldRestrictOperationsWithSessionSelector) {
      this.allowDropdownToggle = false;
    }

    // Whenever we have our goal aligned we want to show only our current session and its children
    if (this.parentItem && this.currentSession) {
      this.sessions = getSessionAndOneLevelChildren(this.sessions, this.parentItem.sessionId);

      const currentGoalAndParentItemAreInSharedSession = this.currentSession.id === this.parentItem.sessionId;

      this.filterWithHostId = currentGoalAndParentItemAreInSharedSession;
      this.subTreeBuild = this.sessionIdMap[this.parentItem.sessionId].parentId && !currentGoalAndParentItemAreInSharedSession;
    }

    // We want to have only OPEN sessions, however if a session is CLOSED or LOCKED we want to check if there is an open session in its children and show it in the tree
    if (this.enableItemDisabledState) {
      this.addPermissionsToSessions();
    }

    this.loading = false;
  }

  showCurrentSession(): void {
    this.sessionSearch = "";
    this.isSearchInputVisible = false;
  }

  hideCurrentSession(): void {
    setTimeout(() => {
      const search = document.getElementById("sessionSearch");
      search?.focus();
    });
    this.sessionSearch = "";
    this.isSearchInputVisible = true;
  }

  showSessionSelector(sessionSearch: string): void {
    this.sessionSearch = sessionSearch;
    if (this.sessionSearch?.length > 0 || this.isSearchInputVisible) {
      this.isSearchInputVisible = true;
    } else {
      this.isSearchInputVisible = false;
    }
  }

  selectSession(sessionId: string): void {
    this.currentSession = this.sessionIdMap[sessionId];
    this.onSelection({ sessionId: sessionId });
    this.isSearchInputVisible = false;
  }

  private addPermissionsToSessions(): void {
    this.fillWithPermissions();
    const sessionsWithPermissions = this.sessions.filter((session) => session.userHasPermissionToCreate);

    if (sessionsWithPermissions.length === 1 && !this.currentSession) {
      this.selectSession(sessionsWithPermissions[0].id);
    }
  }

  private fillWithPermissions(): void {
    for (const session of this.sessions as IPlanningSessionWithChildren[]) {
      session.children = getChildren(session, this.sessions as IPlanningSessionWithChildren[]);
    }

    for (const session of this.sessions as IPlanningSessionWithChildren[]) {
      if (session.status === PlanningSessionStatus.OPEN) {
        session.userHasPermissionToCreate = this.settlePermissions(session, this.currentUser);
      } else if (this.checkBranchStatus(session)) {
        session.userHasPermissionToCreate = false;
      } else {
        this.sessions = this.sessions.filter((s) => s !== session);
      }
    }
  }

  private checkBranchStatus(session: IPlanningSessionWithChildren): boolean {
    if (session.status === PlanningSessionStatus.OPEN) return true;
    if (!session.children.length) return false;

    for (const child of session.children) {
      return this.checkBranchStatus(child);
    }
  }

  private settlePermissions = (session: IPlanningSession, user: IUser): boolean => {
    return allowedActionsSync(user, session.access).includes("create");
  };
}

const getChildren = (sessionItem: IPlanningSessionWithChildren, sessions: IPlanningSessionWithChildren[]): IPlanningSessionWithChildren[] => {
  const children: IPlanningSessionWithChildren[] = [];

  const childrenIds = sessions
    .filter((s) => s.parentId === sessionItem.id)
    .sort(byEndDescAndStartAsc)
    .map((s) => s.id);

  if (!childrenIds.length) {
    return children;
  }

  childrenIds.forEach((childId) => {
    children.push(sessions.find((session) => session.id === childId));
  });

  return children;
};

export const SessionSelectorDropdownComponent: IComponentOptions = {
  controller: SessionSelectorDropdownCtrl,
  templateUrl: require("./session-selector-dropdown.jade"),
  bindings: {
    currentSession: "<",
    goal: "<",
    parentItem: "<",
    onSelection: "&",
    enableItemDisabledState: "<?",
  },
};
